import type { ReactNode } from 'react';
import { BaseModal, FormActions, FormField, FormSectionTitle } from '@practium/ui';
import { useValuesForm, type ValuesRecord } from '../../hooks/useValuesForm';
import { applyScale, removeScale } from '../../utils/format';
import { ValueInput } from './ValueInput';
import styles from '../Sections/Sections.module.css';

interface ValueField {
	key: string;
	label: string;
}

interface ValuesFormModalProps {
	title: string;
	subtitle?: string;
	sectionTitle: string;
	fields: ValueField[];
	initialValues: ValuesRecord;
	scale?: number;
	max?: number;
	maxMessage?: string;
	suffix?: string;
	isSaving: boolean;
	children?: ReactNode;
	onSubmit: (values: Record<string, number>) => Promise<boolean>;
	onClose: () => void;
}

export const ValuesFormModal = ({
	title,
	subtitle,
	sectionTitle,
	fields,
	initialValues,
	scale = 1,
	max,
	maxMessage,
	suffix,
	isSaving,
	children,
	onSubmit,
	onClose,
}: ValuesFormModalProps) => {
	const { values, errors, setValue, submit } = useValuesForm({ initialValues, max, maxMessage, onSubmit, onClose });

	return (
		<BaseModal
			isOpen
			wide={fields.length > 6}
			title={title}
			subtitle={subtitle}
			isLoading={isSaving}
			onClose={onClose}
			actions={<FormActions onCancel={onClose} onSave={submit} isLoading={isSaving} />}
		>
			<div className={styles.form}>
				{children}
				<div className={styles.formSection}>
					<FormSectionTitle>{sectionTitle}</FormSectionTitle>
					<div className={styles.valuesGrid}>
						{fields.map((field) => (
							<FormField key={field.key} label={field.label} required error={errors[field.key]}>
								<ValueInput
									label={field.label}
									suffix={suffix}
									decimals={scale === 1 ? undefined : 2}
									hasError={!!errors[field.key]}
									value={applyScale(values[field.key], scale)}
									onChange={(value) => setValue(field.key, removeScale(value, scale))}
								/>
							</FormField>
						))}
					</div>
				</div>
			</div>
		</BaseModal>
	);
};
